import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusPill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { PhotoPicker } from "@/components/PhotoPicker";
import { MaintenancePhotos } from "@/components/MaintenancePhotos";
import { uploadMaintenancePhotos } from "@/lib/upload";
import { Wrench, Plus } from "lucide-react";
import { toast } from "sonner";

const PRIORITY = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-600" };
const CATEGORIES = ["General", "Plumbing", "Electrical", "HVAC", "Appliance", "Pest", "Structural"];
const EMPTY = { title: "", description: "", category: "General", priority: "medium" };

export default function TenantMaintenance() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);

  const load = useCallback(() => api.get("/maintenance").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title) return toast.error("Please add a short title");
    setSaving(true);
    try {
      const { data } = await api.post("/maintenance", form);
      if (files.length) {
        setProgress(1);
        try { await uploadMaintenancePhotos(data.id, files, setProgress); }
        catch (e) { toast.error(`Request saved, but a photo failed: ${apiErr(e)}`); }
      }
      toast.success(files.length ? `Request submitted with ${files.length} photo(s)` : "Request submitted");
      setOpen(false); setForm(EMPTY); setFiles([]); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); setProgress(null); }
  };

  if (!items) return <Loading />;

  const dialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" data-testid="new-request-btn"><Plus className="h-4 w-4" /> New request</Button></DialogTrigger>
      <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Submit a maintenance request</DialogTitle>
        <DialogDescription>Describe the issue and attach up to 4 photos so your landlord can see the problem.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>What's the issue?</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="request-title" placeholder="Kitchen faucet leaking" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="request-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="request-priority"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Details</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} data-testid="request-description" placeholder="Describe the problem…" /></div>
          <div className="space-y-1.5"><Label>Photos (optional)</Label><PhotoPicker files={files} setFiles={setFiles} max={4} /></div>
          {progress !== null && <div className="space-y-1"><Progress value={progress} className="h-2" data-testid="request-photo-progress" /><p className="text-[11px] text-slate-500">Uploading photos… {progress}%</p></div>}
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} className="w-full" data-testid="request-submit">{saving ? "Submitting…" : "Submit request"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div>
      <PageHeader title="Maintenance" subtitle="Submit and track repair requests for your home." action={dialog} />

      {items.length === 0 ? (
        <EmptyState icon={Wrench} title="No requests yet" subtitle="Something need fixing? Submit a request and your landlord will take care of it." testid="tenant-maint-empty"
          action={<Button onClick={() => setOpen(true)} className="gap-2"><Plus className="h-4 w-4" /> New request</Button>} />
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`tenant-maint-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-slate-900">{m.title}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${PRIORITY[m.priority]}`}>{m.priority}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{m.description}</p>
                  <div className="text-xs text-slate-400 mt-2">{m.category} · {new Date(m.created_at).toLocaleDateString()}</div>
                  {m.resolution_note && m.status === "completed" && (
                    <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 inline-block">{m.resolution_note}</div>
                  )}
                  {m.status !== "completed" || m.photos?.length ? <MaintenancePhotos item={m} role="tenant" onChange={load} /> : null}
                </div>
                <StatusPill status={m.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
